import React, { useMemo } from 'react';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Typography from '@mui/material/Typography';
import type { StepTranslation, StepIconType, TranslationSegment } from './translation.types';
import type { Checkpoint } from '@/graph/domain/types';
import { SegmentRenderer } from './SegmentRenderer';
import { StepIcons } from './StepIcons';
import { CheckpointList } from '@components/modals/AddStepModal/CheckpointList';
import { CycleBracket } from './CycleBracket';

// ─── Error Boundary Component ────────────────────────────────────────────────
const SegmentRenderErrorFallback: React.FC<{ error: Error }> = ({ error }) => (
  <Typography variant="caption" sx={{ color: '#dc2626' }}>
    Failed to render field
  </Typography>
);

// ─── Helper Functions ────────────────────────────────────────────────────────
const mapCheckpointDef = (cp: any): Checkpoint => ({
  id: cp.id,
  checkpointName: cp.name,
  fieldType: cp.fieldType,
  required: cp.mandatory,
  allowMultiple: cp.allowMultiple ?? false,
  allowSpecialCharacters: cp.allowSpecialCharacters ?? false,
  uniqueAcrossWorkspace: cp.uniqueAcrossWorkspace ?? false,
  ...(typeof cp.acceptanceMin === 'number' ? { rangeMin: cp.acceptanceMin } : {}),
  ...(typeof cp.acceptanceMax === 'number' ? { rangeMax: cp.acceptanceMax } : {}),
  ...(cp.units ? { units: cp.units } : {}),
  ...(cp.unitSource ? { unitSource: cp.unitSource } : {}),
  ...(cp.unitCategory ? { unitCategory: cp.unitCategory } : {}),
  ...(cp.linkMultiple !== undefined ? { linkMultiple: cp.linkMultiple } : {}),
  ...(cp.allowedEntityTypes ? { allowedEntityTypes: cp.allowedEntityTypes } : {}),
  ...(cp.dropdown ? { dropdown: cp.dropdown as Checkpoint['dropdown'] } : {}),
  ...(cp.referenceFile ? { referenceFile: cp.referenceFile } : {}),
  ...(cp.referenceSpreadsheetFile ? { referenceSpreadsheetFile: cp.referenceSpreadsheetFile } : {}),
  ...(cp.children ? { children: cp.children.map(mapCheckpointDef) } : {}),
  // Preserve runtime checkpoint values (execute / analysis filled data)
  ...(cp.value !== undefined ? ({ value: cp.value } as object) : {}),
});

const getSegmentKey = (seg: TranslationSegment, index: number): string => {
  const segment = seg as any;
  switch (segment.kind) {
    case 'text':
      return `text:${index}:${segment.value}`;
    case 'field':
      return `field:${index}:${segment.paramName}:${String(segment.value ?? '')}`;
    case 'dropdown':
      return `dropdown:${index}:${segment.paramName}:${segment.value}`;
    case 'material-ref':
      return `material-ref:${segment.materialName}:${segment.containerName}:${segment.constituentExpr}:${segment.showConstituentToggle !== false}:${segment.renderVariant ?? 'default'}:${(segment.constituents ?? []).map((c: any) => `${c.name}:${Number(c.weight).toFixed(6)}`).join('|')}`;
    case 'table':
      return `table:${index}:${segment.paramName}`;
    default:
      return `${segment.kind ?? 'segment'}:${index}`;
  }
};

const toTitleCase = (value?: string | null): string => {
  if (!value) return '-';
  const normalized = value.trim();
  if (!normalized) return '-';
  return normalized.charAt(0).toUpperCase() + normalized.slice(1).toLowerCase();
};

const userLabel = (u: any): string => {
  if (!u || typeof u !== 'object') return '-';
  const name = String(u.name || '').trim();
  if (name && name !== 'undefined') return name;
  const email = String(u.email || '').trim();
  if (email && email !== 'undefined') return email;
  const id = String(u._id || u.id || '').trim();
  if (id && id !== 'undefined') return id;
  return '-';
};

interface StepTranslationRowProps {
  translation: StepTranslation;
  highlighted: boolean;
  highlightedStepIds?: Set<string>;
  editable: boolean;
  activeIconType?: StepIconType | null;
  previewMode?: boolean;
  showIcons?: boolean;
  selectable?: boolean;
  selected?: boolean;
  /** Workspace ID used to resolve signed URLs for checkpoint reference files */
  workspaceId?: string;
  onSelectionToggle?: (nodeId: string) => void;
  onStepClicked?: (nodeId: string) => void;
  onParameterChanged?: (nodeId: string, paramName: string, value: unknown) => void;
  onCheckpointChanged?: (nodeId: string, cpId: string, value: unknown) => void;
  onIconClicked?: (nodeId: string, iconType: StepIconType) => void;
  onEditClicked?: (nodeId: string) => void;
}

// ─── Render Icon Details ────────────────────────────────────────────────────
const renderIconDetails = (
  activeIconType: StepIconType | null | undefined,
  notes: string | null | undefined,
  approvers?: any,
  notifications?: any
): React.ReactNode => {
  if (!activeIconType) return null;

  if (activeIconType === 'notes') {
    return (
      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
        {notes?.trim() || 'No notes for this step.'}
      </Typography>
    );
  }

  if (activeIconType === 'approval') {
    if (!approvers?.users?.length) {
      return (
        <Typography variant="body2">
          No approvers configured for this step.
        </Typography>
      );
    }

    const approverNames = approvers.users.map(userLabel).join(', ');
    return (
      <Box>
        {approvers?.approverType && (
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            <strong>Approval Type:</strong> {toTitleCase(approvers.approverType)}
          </Typography>
        )}
        <Typography variant="body2">
          <strong>Approvers:</strong> {approverNames}
        </Typography>
      </Box>
    );
  }

  // notifications
  if (!notifications?.users?.length) {
    return (
      <Typography variant="body2">
        No notifications configured for this step.
      </Typography>
    );
  }

  const userNames = notifications.users.map(userLabel).join(', ');
  return (
    <Box>
      {notifications?.notificationType && (
        <Typography variant="body2" sx={{ mb: 0.5 }}>
          <strong>Notification Type:</strong> {toTitleCase(notifications.notificationType)}
        </Typography>
      )}
      <Typography variant="body2">
        <strong>Users:</strong> {userNames}
      </Typography>
    </Box>
  );
};

/**
 * Safe wrapper for SegmentRenderer with error boundary
 */
const SafeSegmentRenderer: React.FC<{ segment: TranslationSegment; nodeId: string; highlighted: boolean; onParameterChanged?: (nodeId: string, paramName: string, value: unknown) => void }> = ({
  segment,
  nodeId,
  highlighted,
  onParameterChanged,
}) => {
  try {
    return (
      <SegmentRenderer
        segment={segment}
        nodeId={nodeId}
        highlighted={highlighted}
        onParameterChanged={onParameterChanged}
      />
    );
  } catch (error) {
    console.error('SegmentRenderer error:', error);
    return <SegmentRenderErrorFallback error={error instanceof Error ? error : new Error(String(error))} />;
  }
};

// ─── Main Component ─────────────────────────────────────────────────────────
export const StepTranslationRow: React.FC<StepTranslationRowProps> = React.memo(({
  translation: t,
  highlighted,
  highlightedStepIds = new Set(),
  editable,
  activeIconType = null,
  previewMode = false,
  showIcons = true,
  selectable = false,
  selected = false,
  workspaceId,
  onSelectionToggle,
  ...handlers
}) => {
  // ── Memoized computations ────────────────────────────────────────────────
  const checkpoints = useMemo(() => {
    if (!Array.isArray(t.checkpoints)) return [];
    return t.checkpoints.map(mapCheckpointDef);
  }, [t.checkpoints]);

  const isClickable = !previewMode && Boolean(handlers.onStepClicked);

  return (
    <Box
      onClick={isClickable ? (e) => { e.stopPropagation(); handlers.onStepClicked?.(t.nodeId); } : undefined}
      sx={{
        p: '8px 12px',
        borderBottom: '1px solid #eee',
        cursor: isClickable ? 'pointer' : 'default',
        bgcolor: selected ? '#e0f2fe' : highlighted ? '#dbeafe' : 'transparent',
        borderLeft: highlighted ? '3px solid #2563eb' : selected ? '3px solid #0ea5e9' : 'none',
        '&:hover': {
          bgcolor: isClickable
            ? (selected ? '#e0f2fe' : highlighted ? '#dbeafe' : '#f0f7ff')
            : (selected ? '#e0f2fe' : highlighted ? '#dbeafe' : 'transparent')
        },
        display: 'flex',
        alignItems: 'flex-start',
        gap: 0.5,
      }}
    >
      {selectable && (
        <Checkbox
          size="small"
          checked={selected}
          onClick={(e) => e.stopPropagation()}
          onChange={() => onSelectionToggle?.(t.nodeId)}
          sx={{ p: 0, mt: '2px' }}
        />
      )}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {/* Step sentence with inline fields */}
        <Box sx={{ fontSize: '0.875rem', lineHeight: 2 }}>
          <strong>{t.stepNumber}.</strong>{' '}
          {t.segments.map((seg, i) => (
            <SafeSegmentRenderer
              key={getSegmentKey(seg, i)}
              segment={seg}
              nodeId={t.nodeId}
              highlighted={highlighted || selected}
              onParameterChanged={handlers.onParameterChanged}
            />
          ))}
        </Box>

        {/* Compound sub-steps */}
        {t.subSteps && t.subSteps.length > 0 && (
          <Box sx={{ pl: '20px', borderLeft: '2px solid #e5e7eb', mt: 0.5 }}>
            {/* Render all cycle infos with partitioned sub-steps */}
            {t.cycleInfos && t.cycleInfos.length > 0 ? (
              (() => {
                // Build map of step indices to cycles
                const indexToCycle = new Map<number, { cycleIndex: number; cycleInfo: typeof t.cycleInfos[0] }>();
                t.cycleInfos.forEach((cycleInfo, cycleIndex) => {
                  for (let i = cycleInfo.startIndex; i <= cycleInfo.endIndex; i++) {
                    indexToCycle.set(i, { cycleIndex, cycleInfo });
                  }
                });

                // Partition sub-steps into segments: loose and cycled
                const segments: Array<'loose' | 'cycled'> = [];
                const cycledByIndex = new Map<number, typeof t.cycleInfos[0]>();

                t.subSteps!.forEach((_, idx) => {
                  if (indexToCycle.has(idx)) {
                    segments.push('cycled');
                    cycledByIndex.set(idx, indexToCycle.get(idx)!.cycleInfo);
                  } else {
                    segments.push('loose');
                  }
                });

                // Render: walk through segments, group consecutive loose/cycled
                const result: React.ReactNode[] = [];
                let i = 0;
                while (i < (t.subSteps?.length ?? 0)) {
                  const sub = t.subSteps![i];
                  if (segments[i] === 'loose') {
                    // Render loose sub-step
                    result.push(
                      <StepTranslationRow
                        key={sub.nodeId}
                        translation={sub}
                        highlighted={highlightedStepIds.has(sub.nodeId)}
                        highlightedStepIds={highlightedStepIds as Set<string>}
                        editable={editable}
                        previewMode={previewMode}
                        showIcons={showIcons}
                        workspaceId={workspaceId}
                        {...handlers}
                      />
                    );
                    i++;
                  } else {
                    // Collect consecutive cycled steps belonging to same cycle
                    const cycleInfo = cycledByIndex.get(i)!;
                    const cycleSteps: typeof t.subSteps = [];
                    const cycleStartIndex = i;
                    while (i < (t.subSteps?.length ?? 0) && cycledByIndex.get(i) === cycleInfo) {
                      cycleSteps.push(t.subSteps![i]);
                      i++;
                    }

                    // Find cycle index
                    const cycleIndex = t.cycleInfos!.findIndex((c) => c.id === cycleInfo.id);

                    // Render bracket with cycled steps
                    result.push(
                      <CycleBracket
                        key={cycleInfo.id}
                        id={cycleInfo.id}
                        range={cycleInfo.range}
                        count={cycleInfo.count}
                        cycleIndex={cycleIndex}
                      >
                        {cycleSteps.map((cycleStep) => (
                          <StepTranslationRow
                            key={cycleStep.nodeId}
                            translation={cycleStep}
                            highlighted={highlightedStepIds.has(cycleStep.nodeId)}
                            highlightedStepIds={highlightedStepIds as Set<string>}
                            editable={editable}
                            previewMode={previewMode}
                            showIcons={showIcons}
                            workspaceId={workspaceId}
                            {...handlers}
                          />
                        ))}
                      </CycleBracket>
                    );
                  }
                }
                return result;
              })()
            ) : (
              // No cycles, render all sub-steps flat
              t.subSteps.map((sub) => (
                <StepTranslationRow
                  key={sub.nodeId}
                  translation={sub}
                  highlighted={highlightedStepIds.has(sub.nodeId)}
                  highlightedStepIds={highlightedStepIds as Set<string>}
                  editable={editable}
                  previewMode={previewMode}
                  showIcons={showIcons}
                  workspaceId={workspaceId}
                  {...handlers}
                />
              ))
            )}
          </Box>
        )}

        {/* Checkpoints */}
        {checkpoints.length > 0 && (
          <Box
            onClick={(e) => e.stopPropagation()}
            sx={{ mt: 0.5 }}
          >
            <CheckpointList
              checkpoints={checkpoints}
              mode="preview"
              workspaceId={workspaceId}
            />
          </Box>
        )}

        {/* Step icons row */}
        {showIcons && (
          <StepIcons
            icons={t.icons}
            nodeId={t.nodeId}
            editable={editable}
            activeIconType={activeIconType}
            onIconClicked={handlers.onIconClicked ?? (() => undefined)}
            onEditClicked={handlers.onEditClicked}
          />
        )}

        {/* Icon details panel */}
        {showIcons && activeIconType && (
          <Box
            onClick={(e) => e.stopPropagation()}
            sx={{
              mt: 0.75,
              p: 1,
              border: '1px solid #dbeafe',
              borderRadius: 1,
              bgcolor: '#f8fbff',
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: '#1e3a8a',
                fontWeight: 600,
                textTransform: 'capitalize',
                display: 'block',
                mb: 0.5
              }}
            >
              {activeIconType}
            </Typography>
            {renderIconDetails(activeIconType, t.notes, t.approvers, t.notifications)}
          </Box>
        )}
      </Box>
    </Box>
  );
}, (prevProps, nextProps) => {
  // Memoization comparison - return true if props are equal (skip render)
  return (
    prevProps.translation === nextProps.translation &&
    prevProps.highlighted === nextProps.highlighted &&
    prevProps.selected === nextProps.selected &&
    prevProps.editable === nextProps.editable &&
    prevProps.activeIconType === nextProps.activeIconType &&
    prevProps.previewMode === nextProps.previewMode &&
    prevProps.showIcons === nextProps.showIcons &&
    prevProps.selectable === nextProps.selectable
  );
});
